/**
 * Marketing reports listing page.
 */
import {
  Megaphone,
  Calendar,
  Clock,
  Rows3,
  Filter,
  TrendingUp,
  BarChart3,
  TableProperties,
} from 'lucide-react';
import { ReportsPage } from '@/components/reports/ReportsPage';
import { ConfigBadge } from '@/components/reports/ConfigBadge';
import { PRESET_LABELS, INTERVAL_LABELS } from '@/lib/reports.types';
import { countFilters } from '@/lib/filters';

const iconCls = 'w-3 h-3 shrink-0';

function renderBadges(config: Record<string, unknown>) {
  const mktBreakdown = ((config.rowBreakdown as string) || (config.breakdown as string) || '');
  const rawMetrics = config.selectedMetrics ?? config.metrics ?? '';
  const selectedMetrics: string[] = Array.isArray(rawMetrics)
    ? rawMetrics
    : typeof rawMetrics === 'string' && rawMetrics ? rawMetrics.split(',').filter(Boolean) : [];
  const chart = ((config.chartType as string) || (config.chart as string) || 'pivot');
  const preset = ((config.datePreset as string) || (config.preset as string) || '');
  const startDate = ((config.startDate as string) || (config.start as string) || '');
  const endDate = ((config.endDate as string) || (config.end as string) || '');
  const interval = ((config.timeInterval as string) || (config.interval as string) || 'd');
  const filterCount = countFilters(config.filters);

  const dateLabel = preset
    ? PRESET_LABELS[preset] || preset.replace(/_/g, ' ')
    : startDate && endDate ? `${startDate} → ${endDate}` : '';

  return (
    <>
      <ConfigBadge
        icon={chart === 'pivot' ? <TableProperties className={iconCls} /> : <BarChart3 className={iconCls} />}
        text={chart === 'pivot' ? 'Pivot' : 'Time Series'}
        color="sky"
      />
      {dateLabel && <ConfigBadge icon={<Calendar className={iconCls} />} text={dateLabel} color="amber" />}
      {interval && interval !== 'd' && interval !== 'days' && (
        <ConfigBadge icon={<Clock className={iconCls} />} text={INTERVAL_LABELS[interval] || interval} color="sky" />
      )}
      {mktBreakdown && <ConfigBadge icon={<Rows3 className={iconCls} />} text={mktBreakdown.replace(/_/g, ' ')} color="emerald" />}
      {selectedMetrics.length > 0 && (
        <ConfigBadge icon={<TrendingUp className={iconCls} />} text={`${selectedMetrics.length} metric${selectedMetrics.length > 1 ? 's' : ''}`} color="brand" />
      )}
      {filterCount > 0 && <ConfigBadge icon={<Filter className={iconCls} />} text={`${filterCount} filter${filterCount > 1 ? 's' : ''}`} color="rose" />}
    </>
  );
}

export function MarketingReports() {
  return (
    <ReportsPage
      sectionId="marketing"
      sectionRoute="/marketing"
      title="Marketing"
      subtitle="Analyze campaign performance and ad spend metrics"
      emptyIcon={Megaphone}
      emptyDescription="Create your first marketing report to start analyzing campaigns"
      showFolders
      accentColor="emerald"
      cardIcon={<Megaphone className="w-4 h-4 text-emerald-500" />}
      cardIconBgClass="bg-emerald-50"
      renderBadges={renderBadges}
    />
  );
}
